import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Copy, Check, Users,
  Sparkles, AlertCircle, ArrowRight, ArrowDown, Zap,
  Play, ShieldCheck, Info, RotateCcw,
  MessageSquare, ShoppingBag, Clock,
  BarChart3, Palette, Database, Layers, Inbox
} from 'lucide-react';
import './AffiliatePortal.css';

// Calculation Tooltip Component with fixed viewport positioning and auto-flip
function CalcTooltip({ calc }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });
  const btnRef = useRef(null);

  const calculatePosition = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const tooltipHeight = 290;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < tooltipHeight && rect.top > tooltipHeight;

    // Horizontal clamping so tooltip never overflows viewport edges
    let left = rect.left + rect.width / 2;
    left = Math.max(160, Math.min(window.innerWidth - 160, left));

    setPos({
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left,
      openUp
    });
  };

  const handleMouseEnter = () => {
    calculatePosition();
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    calculatePosition();
    setOpen((prev) => !prev);
  };

  if (!calc) return null;

  return (
    <div 
      className="calc-tooltip-anchor" 
      ref={btnRef} 
      onMouseEnter={handleMouseEnter} 
      onMouseLeave={handleMouseLeave}
    >
      <button 
        type="button" 
        className="calc-info-trigger" 
        onClick={handleClick}
        aria-label="View calculation formula and parameters"
      >
        <Info size={12} />
      </button>

      {open && (
        <div 
          className={`calc-tooltip-popover ${pos.openUp ? 'popover-up' : 'popover-down'}`}
          style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
        >
          {/* Header */}
          <div className="tooltip-header">
            <span className="tooltip-eyebrow">Exact Formula & Terms</span>
            <h5 className="tooltip-title">{calc.title}</h5>
          </div>

          {/* Formula Banner */}
          <div className="tooltip-formula-row">
            <span className="tooltip-lbl">Formula</span>
            <code className="tooltip-code">{calc.formula}</code>
          </div>

          {/* Variables Card */}
          <div className="tooltip-vars-card">
            <span className="tooltip-lbl">Variables</span>
            <div className="tooltip-vars-list">
              {calc.vars.map((v) => (
                <div key={v.sym} className="tooltip-var-row">
                  <div className="var-left">
                    <span className="var-badge">{v.sym}</span>
                    <span className="var-desc">{v.desc}</span>
                  </div>
                  <span className="var-val">{v.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Math Computation Card */}
          <div className="tooltip-math-card">
            <span className="tooltip-lbl">Computation</span>
            <div className="math-equation">{calc.math}</div>
            {calc.breakdown && <div className="math-breakdown-sub">{calc.breakdown}</div>}
          </div>

          {/* Collection Terms Footer */}
          {calc.note && (
            <div className="tooltip-footer-terms">
              <span className="terms-lbl">Terms:</span> {calc.note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AffiliatePortal() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const codeInUrl = queryParams.get('code') || queryParams.get('ref') || queryParams.get('via');

  const [inputCode, setInputCode] = useState(codeInUrl || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Table Category Filter Pill: 'all', 'monthly', 'yearly', 'lifetime'
  const [tableFilter, setTableFilter] = useState('all');

  // Public Self-Registration State
  const [regForm, setRegForm] = useState({
    name: '',
    email: '',
    referralCode: '',
    payoutMethod: 'paypal',
    payoutDetails: ''
  });
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState(null);

  // Simulation state
  const [simPlan, setSimPlan] = useState('starter_monthly');
  const [simCycle, setSimCycle] = useState('monthly');
  const [simAmount, setSimAmount] = useState(24);
  const [simLoading, setSimLoading] = useState(false);
  const [simMsg, setSimMsg] = useState(null);

  // Safe API Fetcher that handles HTML responses gracefully
  const safeApiFetch = async (url, options = {}) => {
    const res = await fetch(url, options);
    const text = await res.text();
    let json = null;
    try {
      json = JSON.parse(text);
    } catch (parseErr) {
      if (text.includes('<!DOCTYPE') || text.includes('<html')) {
        throw new Error(
          'Server returned HTML instead of JSON. If your backend terminal was already running before the affiliate routes were added, please restart it (Ctrl+C and npm run dev) so Node loads the new API endpoints.'
        );
      }
      throw new Error(text || `Failed to communicate with server (HTTP ${res.status})`);
    }
    if (!res.ok) {
      throw new Error(json?.error || `Request failed with status ${res.status}`);
    }
    return json;
  };

  const fetchStats = useCallback(async (codeToFetch, resetSimOnLoad = false) => {
    if (!codeToFetch || !codeToFetch.trim()) return;
    try {
      setLoading(true);
      setError(null);
      const url = resetSimOnLoad
        ? `/api/affiliates/portal?code=${encodeURIComponent(codeToFetch.trim())}&reset_sim=true`
        : `/api/affiliates/portal?code=${encodeURIComponent(codeToFetch.trim())}`;
      const json = await safeApiFetch(url);
      setData(json);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (codeInUrl) {
      setInputCode(codeInUrl);
      fetchStats(codeInUrl, true); // Automatically restore simulated test orders on initial load / refresh!
    }
  }, [codeInUrl, fetchStats]);

  // Handle Public Self Registration
  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      setRegLoading(true);
      setRegError(null);
      const json = await safeApiFetch('/api/affiliates/public-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regForm)
      });

      const newCode = json.affiliate.referral_code;
      setInputCode(newCode);
      navigate(`/affiliates?code=${newCode}`, { replace: true });
      fetchStats(newCode);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setRegError(err.message);
    } finally {
      setRegLoading(false);
    }
  };

  // Copy referral link
  const handleCopyLink = () => {
    if (!data?.affiliate?.referral_code) return;
    const origin = window.location.origin;
    const link = `${origin}/?ref=${data.affiliate.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Run Test Order Simulation
  const handleSimulateOrder = async () => {
    if (!data?.affiliate?.referral_code) return;
    try {
      setSimLoading(true);
      setSimMsg(null);

      const json = await safeApiFetch('/api/affiliates/simulate-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referralCode: data.affiliate.referral_code,
          planId: simPlan,
          billingCycle: simCycle,
          grossAmount: simAmount,
          customerEmail: `customer_${Math.floor(1000 + Math.random() * 9000)}@test.com`
        })
      });

      setSimMsg(`✓ Test sale recorded! Commission: $${json.commission.commission_amount} (${json.commission.commission_rate * 100}%)`);
      fetchStats(data.affiliate.referral_code);
    } catch (err) {
      alert(`Simulation error: ${err.message}`);
    } finally {
      setSimLoading(false);
    }
  };


  // Reset / Restore simulated test data
  const handleResetSimulation = async () => {
    if (!data?.affiliate?.referral_code) return;
    try {
      setSimLoading(true);
      const json = await safeApiFetch('/api/affiliates/reset-simulation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referralCode: data.affiliate.referral_code })
      });
      setSimMsg(`✓ ${json.message}`);
      fetchStats(data.affiliate.referral_code, false);
    } catch (err) {
      alert(`Error resetting simulation: ${err.message}`);
    } finally {
      setSimLoading(false);
    }
  };

  const handlePlanSelect = (e) => {
    const val = e.target.value;
    setSimPlan(val);
    switch (val) {
      case 'starter_monthly':
        setSimCycle('monthly');
        setSimAmount(24);
        break;
      case 'starter_yearly':
        setSimCycle('yearly');
        setSimAmount(228);
        break;
      case 'pro_monthly':
        setSimCycle('monthly');
        setSimAmount(38);
        break;
      case 'pro_yearly':
        setSimCycle('yearly');
        setSimAmount(360);
        break;
      case 'unlimited_monthly':
        setSimCycle('monthly');
        setSimAmount(125);
        break;
      case 'unlimited_yearly':
        setSimCycle('yearly');
        setSimAmount(1200);
        break;
      case 'lifetime':
        setSimCycle('lifetime');
        setSimAmount(947);
        break;
      default:
        break;
    }
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.smushlabs.ai';

  const commissionSections = [
    {
      id: 'monthly',
      category: 'Monthly Plans — Commission Paid for First 12 Months',
      plans: [
        {
          name: 'Starter Monthly',
          price: '$24 / mo',
          popular: false,
          duration: 'First 12 paid months',
          tier1: {
            each: '$8.40 / mo',
            totalSub: 'Up to $504.00 total for 5 users',
            calc: {
              title: 'Starter Monthly — Tier 1 (First 5 Customers)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Number of Customers in Tier 1', val: '5 customers' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$24.00 / month' },
                { sym: 'R', desc: 'Tier 1 Commission Rate', val: '35% (0.35)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '5 × $24.00 × 0.35 × 12 = $504.00 total potential',
              breakdown: 'Per-user commission: $24.00 × 0.35 = $8.40 / mo ($100.80 / yr each)',
              note: 'Commission is paid monthly as Lemon Squeezy collects each payment.'
            }
          },
          tier2: {
            each: '$7.20 / mo',
            totalSub: 'Up to $864.00 total for 10 users',
            calc: {
              title: 'Starter Monthly — Tier 2 (10 Users, #6–15)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 2 bracket', val: '10 customers' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$24.00 / month' },
                { sym: 'R', desc: 'Tier 2 Commission Rate', val: '30% (0.30)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '10 × $24.00 × 0.30 × 12 = $864.00 total potential',
              breakdown: 'Per-user commission: $24.00 × 0.30 = $7.20 / mo ($86.40 / yr each)',
              note: 'Commission is paid monthly as Lemon Squeezy collects each payment.'
            }
          },
          tier3: {
            each: '$6.00 / mo',
            totalSub: 'Max $72.00/yr each • $720.00 for 10 users',
            calc: {
              title: 'Starter Monthly — Tier 3 (Customer 16+)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Example customer count', val: '1 customer' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$24.00 / month' },
                { sym: 'R', desc: 'Tier 3 Commission Rate', val: '25% (0.25)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '1 × $24.00 × 0.25 × 12 = $72.00 / customer ($6.00 / mo)',
              breakdown: 'Benchmark for 10 customers: 10 × $72.00 = $720.00',
              note: 'No customer ceiling for Tier 3. Earn on every referral.'
            }
          }
        },
        {
          name: 'Pro Monthly',
          price: '$38 / mo',
          popular: true,
          duration: 'First 12 paid months',
          tier1: {
            each: '$13.30 / mo',
            totalSub: 'Up to $798.00 total for 5 users',
            calc: {
              title: 'Pro Monthly — Tier 1 (First 5 Customers)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Number of Customers in Tier 1', val: '5 customers' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$38.00 / month' },
                { sym: 'R', desc: 'Tier 1 Commission Rate', val: '35% (0.35)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '5 × $38.00 × 0.35 × 12 = $798.00 total potential',
              breakdown: 'Per-user commission: $38.00 × 0.35 = $13.30 / mo ($159.60 / yr each)',
              note: 'Commission is paid monthly as Lemon Squeezy collects each payment.'
            }
          },
          tier2: {
            each: '$11.40 / mo',
            totalSub: 'Up to $1,368.00 total for 10 users',
            calc: {
              title: 'Pro Monthly — Tier 2 (10 Users, #6–15)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 2 bracket', val: '10 customers' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$38.00 / month' },
                { sym: 'R', desc: 'Tier 2 Commission Rate', val: '30% (0.30)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '10 × $38.00 × 0.30 × 12 = $1,368.00 total potential',
              breakdown: 'Per-user commission: $38.00 × 0.30 = $11.40 / mo ($136.80 / yr each)',
              note: 'Commission is paid monthly as Lemon Squeezy collects each payment.'
            }
          },
          tier3: {
            each: '$9.50 / mo',
            totalSub: 'Max $114.00/yr each • $1,140.00 for 10 users',
            calc: {
              title: 'Pro Monthly — Tier 3 (Customer 16+)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Example customer count', val: '1 customer' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$38.00 / month' },
                { sym: 'R', desc: 'Tier 3 Commission Rate', val: '25% (0.25)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '1 × $38.00 × 0.25 × 12 = $114.00 / customer ($9.50 / mo)',
              breakdown: 'Benchmark for 10 customers: 10 × $114.00 = $1,140.00',
              note: 'No customer ceiling for Tier 3. Earn on every referral.'
            }
          }
        },
        {
          name: 'Unlimited Monthly',
          price: '$125 / mo',
          popular: false,
          duration: 'First 12 paid months',
          tier1: {
            each: '$43.75 / mo',
            totalSub: 'Up to $2,625.00 total for 5 users',
            calc: {
              title: 'Unlimited Monthly — Tier 1 (First 5 Customers)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Number of Customers in Tier 1', val: '5 customers' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$125.00 / month' },
                { sym: 'R', desc: 'Tier 1 Commission Rate', val: '35% (0.35)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '5 × $125.00 × 0.35 × 12 = $2,625.00 total potential',
              breakdown: 'Per-user commission: $125.00 × 0.35 = $43.75 / mo ($525.00 / yr each)',
              note: 'Commission is paid monthly as Lemon Squeezy collects each payment.'
            }
          },
          tier2: {
            each: '$37.50 / mo',
            totalSub: 'Up to $4,500.00 total for 10 users',
            calc: {
              title: 'Unlimited Monthly — Tier 2 (10 Users, #6–15)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 2 bracket', val: '10 customers' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$125.00 / month' },
                { sym: 'R', desc: 'Tier 2 Commission Rate', val: '30% (0.30)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '10 × $125.00 × 0.30 × 12 = $4,500.00 total potential',
              breakdown: 'Per-user commission: $125.00 × 0.30 = $37.50 / mo ($450.00 / yr each)',
              note: 'Commission is paid monthly as Lemon Squeezy collects each payment.'
            }
          },
          tier3: {
            each: '$31.25 / mo',
            totalSub: 'Max $375.00/yr each • $3,750.00 for 10 users',
            calc: {
              title: 'Unlimited Monthly — Tier 3 (Customer 16+)',
              formula: 'E = N × P × R × M',
              vars: [
                { sym: 'N', desc: 'Example customer count', val: '1 customer' },
                { sym: 'P', desc: 'Monthly Retail Price', val: '$125.00 / month' },
                { sym: 'R', desc: 'Tier 3 Commission Rate', val: '25% (0.25)' },
                { sym: 'M', desc: 'Eligible Monthly Cycles', val: '12 months' }
              ],
              math: '1 × $125.00 × 0.25 × 12 = $375.00 / customer ($31.25 / mo)',
              breakdown: 'Benchmark for 10 customers: 10 × $375.00 = $3,750.00',
              note: 'No customer ceiling for Tier 3. Earn on every referral.'
            }
          }
        }
      ]
    },
    {
      id: 'yearly',
      category: 'Yearly Plans — Commission Paid on First Annual Invoice',
      plans: [
        {
          name: 'Starter Yearly',
          price: '$228 / yr',
          popular: false,
          duration: '1st annual payment',
          tier1: {
            each: '$79.80',
            totalSub: 'Up to $399.00 total for 5 users',
            calc: {
              title: 'Starter Yearly — Tier 1 (First 5 Customers)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 1', val: '5 customers' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$228.00 / year' },
                { sym: 'R', desc: 'Tier 1 Annual Rate', val: '35% (0.35)' },
                { sym: 'M', desc: 'Annual Payment Invoices', val: '1st payment' }
              ],
              math: '5 × $228.00 × 0.35 = $399.00 total potential',
              breakdown: 'Per-user commission: $228.00 × 0.35 = $79.80',
              note: 'Commission is paid upon collection of customer’s first annual invoice.'
            }
          },
          tier2: {
            each: '$68.40',
            totalSub: 'Up to $684.00 total for 10 users',
            calc: {
              title: 'Starter Yearly — Tier 2 (10 Users, #6–15)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 2', val: '10 customers' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$228.00 / year' },
                { sym: 'R', desc: 'Tier 2 Annual Rate', val: '30% (0.30)' }
              ],
              math: '10 × $228.00 × 0.30 = $684.00 total potential',
              breakdown: 'Per-user commission: $228.00 × 0.30 = $68.40',
              note: 'Commission is paid upon collection of customer’s first annual invoice.'
            }
          },
          tier3: {
            each: '$57.00',
            totalSub: '$570.00 total for 10 users',
            calc: {
              title: 'Starter Yearly — Tier 3 (Customer 16+)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Example customer count', val: '1 customer' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$228.00 / year' },
                { sym: 'R', desc: 'Tier 3 Annual Rate', val: '25% (0.25)' }
              ],
              math: '1 × $228.00 × 0.25 = $57.00 / customer',
              breakdown: 'Benchmark for 10 customers: 10 × $57.00 = $570.00',
              note: 'No customer ceiling for Tier 3. Earn on every referral.'
            }
          }
        },
        {
          name: 'Pro Yearly',
          price: '$360 / yr',
          popular: false,
          duration: '1st annual payment',
          tier1: {
            each: '$126.00',
            totalSub: 'Up to $630.00 total for 5 users',
            calc: {
              title: 'Pro Yearly — Tier 1 (First 5 Customers)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 1', val: '5 customers' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$360.00 / year' },
                { sym: 'R', desc: 'Tier 1 Annual Rate', val: '35% (0.35)' },
                { sym: 'M', desc: 'Annual Payment Invoices', val: '1st payment' }
              ],
              math: '5 × $360.00 × 0.35 = $630.00 total potential',
              breakdown: 'Per-user commission: $360.00 × 0.35 = $126.00',
              note: 'Commission is paid upon collection of customer’s first annual invoice.'
            }
          },
          tier2: {
            each: '$108.00',
            totalSub: 'Up to $1,080.00 total for 10 users',
            calc: {
              title: 'Pro Yearly — Tier 2 (10 Users, #6–15)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 2', val: '10 customers' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$360.00 / year' },
                { sym: 'R', desc: 'Tier 2 Annual Rate', val: '30% (0.30)' }
              ],
              math: '10 × $360.00 × 0.30 = $1,080.00 total potential',
              breakdown: 'Per-user commission: $360.00 × 0.30 = $108.00',
              note: 'Commission is paid upon collection of customer’s first annual invoice.'
            }
          },
          tier3: {
            each: '$90.00',
            totalSub: '$900.00 total for 10 users',
            calc: {
              title: 'Pro Yearly — Tier 3 (Customer 16+)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Example customer count', val: '1 customer' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$360.00 / year' },
                { sym: 'R', desc: 'Tier 3 Annual Rate', val: '25% (0.25)' }
              ],
              math: '1 × $360.00 × 0.25 = $90.00 / customer',
              breakdown: 'Benchmark for 10 customers: 10 × $90.00 = $900.00',
              note: 'No customer ceiling for Tier 3. Earn on every referral.'
            }
          }
        },
        {
          name: 'Unlimited Yearly',
          price: '$1,200 / yr',
          popular: false,
          duration: '1st annual payment',
          tier1: {
            each: '$420.00',
            totalSub: 'Up to $2,100.00 total for 5 users',
            calc: {
              title: 'Unlimited Yearly — Tier 1 (First 5 Customers)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 1', val: '5 customers' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$1,200.00 / year' },
                { sym: 'R', desc: 'Tier 1 Annual Rate', val: '35% (0.35)' },
                { sym: 'M', desc: 'Annual Payment Invoices', val: '1st payment' }
              ],
              math: '5 × $1,200.00 × 0.35 = $2,100.00 total potential',
              breakdown: 'Per-user commission: $1,200.00 × 0.35 = $420.00',
              note: 'Commission is paid upon collection of customer’s first annual invoice.'
            }
          },
          tier2: {
            each: '$360.00',
            totalSub: 'Up to $3,600.00 total for 10 users',
            calc: {
              title: 'Unlimited Yearly — Tier 2 (10 Users, #6–15)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 2', val: '10 customers' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$1,200.00 / year' },
                { sym: 'R', desc: 'Tier 2 Annual Rate', val: '30% (0.30)' }
              ],
              math: '10 × $1,200.00 × 0.30 = $3,600.00 total potential',
              breakdown: 'Per-user commission: $1,200.00 × 0.30 = $360.00',
              note: 'Commission is paid upon collection of customer’s first annual invoice.'
            }
          },
          tier3: {
            each: '$300.00',
            totalSub: '$3,000.00 total for 10 users',
            calc: {
              title: 'Unlimited Yearly — Tier 3 (Customer 16+)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Example customer count', val: '1 customer' },
                { sym: 'P', desc: 'Annual Retail Price', val: '$1,200.00 / year' },
                { sym: 'R', desc: 'Tier 3 Annual Rate', val: '25% (0.25)' }
              ],
              math: '1 × $1,200.00 × 0.25 = $300.00 / customer',
              breakdown: 'Benchmark for 10 customers: 10 × $300.00 = $3,000.00',
              note: 'No customer ceiling for Tier 3. Earn on every referral.'
            }
          }
        }
      ]
    },
    {
      id: 'lifetime',
      category: 'Lifetime License — Paid Once Upon Customer Purchase',
      plans: [
        {
          name: 'Founders Edition',
          price: '$947 once',
          popular: false,
          duration: 'One-time on collection',
          tier1: {
            each: '$236.75',
            totalSub: 'Up to $1,183.75 total for 5 users',
            calc: {
              title: 'Founders Edition — Tier 1 (First 5 Customers)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 1', val: '5 customers' },
                { sym: 'P', desc: 'Lifetime License Price', val: '$947.00' },
                { sym: 'R', desc: 'Tier 1 Lifetime Rate', val: '25% (0.25)' },
                { sym: 'M', desc: 'Payment Type', val: '1-time purchase' }
              ],
              math: '5 × $947.00 × 0.25 = $1,183.75 total potential',
              breakdown: 'Per-user commission: $947.00 × 0.25 = $236.75',
              note: 'Commission is paid once upon successful customer purchase.'
            }
          },
          tier2: {
            each: '$213.08',
            totalSub: 'Up to $2,130.75 total for 10 users',
            calc: {
              title: 'Founders Edition — Tier 2 (10 Users, #6–15)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Customers in Tier 2', val: '10 customers' },
                { sym: 'P', desc: 'Lifetime License Price', val: '$947.00' },
                { sym: 'R', desc: 'Tier 2 Lifetime Rate', val: '22.5% (0.225)' }
              ],
              math: '10 × $947.00 × 0.225 = $2,130.75 total potential',
              breakdown: 'Per-user commission: $947.00 × 0.225 = $213.075',
              note: 'Commission is paid once upon successful customer purchase.'
            }
          },
          tier3: {
            each: '$189.40',
            totalSub: '$1,894.00 total for 10 users',
            calc: {
              title: 'Founders Edition — Tier 3 (Customer 16+)',
              formula: 'E = N × P × R',
              vars: [
                { sym: 'N', desc: 'Example customer count', val: '1 customer' },
                { sym: 'P', desc: 'Lifetime License Price', val: '$947.00' },
                { sym: 'R', desc: 'Tier 3 Lifetime Rate', val: '20% (0.20)' }
              ],
              math: '1 × $947.00 × 0.20 = $189.40 / customer',
              breakdown: 'Benchmark for 10 customers: 10 × $189.40 = $1,894.00',
              note: 'No customer ceiling for Tier 3. Earn on every referral.'
            }
          }
        }
      ]
    }
  ];

  const visibleSections = commissionSections.filter(sec => tableFilter === 'all' || sec.id === tableFilter);

  return (
    <div className="aff-light-page">
      {/* Hero Header exactly matching Launch Deal style */}
      <section className="aff-hero-section">
        <div className="aff-hero-content">
          <span className="aff-kicker">AGENCY PARTNER PROGRAM</span>
          <h1 className="aff-main-title">
            <span className="aff-title-line">Turn your existing clients</span>
            <span className="aff-title-line">into recurring revenue.</span>
          </h1>
          <p className="aff-hero-desc">
            Stop leaving money on the table. Introduce your existing clients to SmushChat—we handle all the training and deployment, while you pocket up to 35% recurring revenue every month.
          </p>
        </div>
      </section>

      <div className="aff-content-container">
        {/* TOP SECTION: TRACK YOUR STATUS (ALWAYS VISIBLE) */}
        <div className={`aff-action-section ${data ? 'has-stats' : ''}`}>
          <div className="track-subform">
            <div className="subform-header">
              <h3>Track Your Performance & Earnings</h3>
              <p>Enter your partner code to inspect real-time attributed clients, active tier, and ledger.</p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (inputCode.trim()) {
                  navigate(`/affiliates?code=${inputCode.trim()}`, { replace: true });
                  fetchStats(inputCode.trim(), true);
                }
              }}
              className="lookup-inline-form"
            >
              <div className="lookup-box">
                <span className="lookup-prefix">smushlabs.ai/?ref=</span>
                <input
                  type="text"
                  placeholder="Enter your partner slug (e.g. alex)"
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value)}
                />
              </div>
              <button type="submit" disabled={loading} className="btn-lookup-action">
                {loading ? 'Loading...' : 'View Stats'} <ArrowRight size={16} />
              </button>
            </form>

            <div className="new-affiliate-cta">
              <span className="cta-question">New Partner?</span>
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById('affiliate-signup');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="btn-signup-anchor"
              >
                Get your partner code <ArrowDown size={14} />
              </button>
            </div>

            {error && (
              <div className="error-badge">
                <AlertCircle size={15} /> {error}
              </div>
            )}

            {/* LIVE STATS DISPLAY */}
            {data && (
              <div className="live-stats-wrapper">
                {/* Top Partner Details Row */}
                <div className="partner-bar">
                  <div className="bar-info">
                    <div className="partner-status-row">
                      <span className="status-dot"></span>
                      <span className="status-tag">Active Partner</span>
                    </div>
                    <h3 className="partner-name">{data.affiliate.name}</h3>
                    <p className="partner-meta">
                      Code: <code>{data.affiliate.referral_code}</code>
                      <span className="meta-sep">•</span>
                      Payout: {data.affiliate.payout_method?.toUpperCase()}
                      {data.affiliate.is_default_fallback && (
                        <span className="fallback-pill">Fallback Partner</span>
                      )}
                    </p>
                  </div>

                  <div className="bar-link">
                    <span className="bar-link-label">Your Partner Link</span>
                    <div className="bar-link-input">
                      <input
                        type="text"
                        readOnly
                        value={`${origin}/?ref=${data.affiliate.referral_code}`}
                      />
                      <button onClick={handleCopyLink} className="btn-mini-copy">
                        {copied ? <Check size={14} className="text-green" /> : <Copy size={14} />}
                        <span>{copied ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* 4 Metric Boxes - Unified Monochromatic Strip */}
                <div className="metrics-quad">
                  <div className="quad-card">
                    <div className="quad-label">Attributed Customers</div>
                    <div className="quad-val">{data.metrics.totalCustomers}</div>
                    <span className="quad-sub">Direct subscribers</span>
                  </div>

                  <div className="quad-card">
                    <div className="quad-label">Pending Settlement</div>
                    <div className="quad-val">${data.metrics.pendingPayout.toFixed(2)}</div>
                    <span className="quad-sub">Awaiting settlement</span>
                  </div>

                  <div className="quad-card">
                    <div className="quad-label">Total Paid Out</div>
                    <div className="quad-val">${data.metrics.totalPaid.toFixed(2)}</div>
                    <span className="quad-sub">Settled earnings</span>
                  </div>

                  <div className="quad-card">
                    <div className="quad-label">All-Time Earnings</div>
                    <div className="quad-val">${data.metrics.totalEarnings.toFixed(2)}</div>
                    <span className="quad-sub">Gross commissions</span>
                  </div>
                </div>

                {/* Tier Progress Bar */}
                <div className="tier-bar-card">
                  <div className="tier-bar-header">
                    <div>
                      <span className="micro-label">Current Tier Rate</span>
                      <strong className="tier-name-display">{data.metrics.currentTier}</strong>
                    </div>
                    {data.metrics.nextTierGoal && (
                      <span className="next-goal-tag">
                        {data.metrics.customersToNextTier} more customer{data.metrics.customersToNextTier === 1 ? '' : 's'} to next bracket
                      </span>
                    )}
                  </div>
                  {data.metrics.nextTierGoal && (
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${Math.min(100, (data.metrics.totalCustomers / data.metrics.nextTierGoal) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* TEST TRANSACTION SIMULATOR */}
                <div className="simulator-bar">
                  <div className="sim-header-row">
                    <div className="sim-title-group">
                      <div className="sim-title-wrap">
                        <Sparkles size={17} className="sim-title-icon" />
                        <h4 className="sim-title">Interactive Partner Commission Simulator</h4>
                      </div>
                      <p className="sim-desc">Simulate a live client order to test real-time commission attribution and tier progression.</p>
                    </div>
                    <span className="sim-badge">Sandboxed Test Bed</span>
                  </div>

                  <div className="sim-restore-notice">
                    <ShieldCheck size={15} className="notice-icon" />
                    <span><strong>Security sandbox:</strong> Simulated test orders automatically get restored on page refresh.</span>
                    {simMsg && <span className="sim-msg-tag">{simMsg}</span>}
                  </div>

                  <div className="sim-actions">
                    <div className="sim-select-wrap">
                      <span className="sim-select-label">Select Plan:</span>
                      <select value={simPlan} onChange={handlePlanSelect} className="sim-plan-select">
                        <option value="starter_monthly">Starter Monthly ($24)</option>
                        <option value="starter_yearly">Starter Yearly ($228)</option>
                        <option value="pro_monthly">Pro Monthly ($38)</option>
                        <option value="pro_yearly">Pro Yearly ($360)</option>
                        <option value="unlimited_monthly">Unlimited Monthly ($125)</option>
                        <option value="unlimited_yearly">Unlimited Yearly ($1,200)</option>
                        <option value="lifetime">Lifetime License ($947)</option>
                      </select>
                    </div>

                    <button onClick={handleSimulateOrder} disabled={simLoading} className="btn-sim-run">
                      <Play size={13} /> Simulate Order (${simAmount})
                    </button>

                    {data.commissions.some(c => c.ls_order_id?.startsWith('test_ord_')) && (
                      <button onClick={handleResetSimulation} disabled={simLoading} className="btn-sim-reset">
                        <RotateCcw size={13} /> Restore Clean Ledger
                      </button>
                    )}
                  </div>
                </div>

                  {/* Live Transaction Ledger */}
                  {data.commissions.length > 0 ? (
                    <div className="ledger-box">
                      <div className="ledger-title-row">
                        <div>
                          <h4>Attributed Transactions Ledger</h4>
                          <span className="ledger-subtext">Monthly plans pay for first 12 months • Annual & Lifetime pay on invoice 1</span>
                        </div>
                        {data.commissions.some(c => c.ls_order_id?.startsWith('test_ord_')) && (
                          <div className="ledger-sim-pill">
                            <span>Simulated orders active • Restored on page refresh</span>
                            <button onClick={handleResetSimulation} className="btn-inline-restore">Restore Now</button>
                          </div>
                        )}
                      </div>

                      <div className="ledger-clean-table-wrapper">
                        <table className="ledger-master-table">
                          <thead>
                            <tr>
                              <th style={{ width: '13%' }}>Date</th>
                              <th style={{ width: '22%' }}>Customer & Sequence</th>
                              <th style={{ width: '23%' }}>Plan / Billing Cycle</th>
                              <th style={{ width: '12%' }}>Gross Sale</th>
                              <th style={{ width: '12%' }}>Commission Rate</th>
                              <th style={{ width: '18%' }}>Partner Share</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.commissions.map((comm) => {
                              const isSim = comm.ls_order_id?.startsWith('test_ord_');
                              const tierNum = comm.customer_tier_index <= 5 ? 1 : comm.customer_tier_index <= 15 ? 2 : 3;
                              return (
                                <tr key={comm.id} className={isSim ? 'row-simulated' : ''}>
                                  <td className="td-ledger-date">
                                    <span className="date-val">{new Date(comm.created_at).toLocaleDateString()}</span>
                                    {isSim && <span className="sim-inline-badge">Simulated</span>}
                                  </td>
                                  <td className="td-ledger-cust">
                                    <div className="cust-row-info">
                                      <span className="cust-seq-chip">Customer #{comm.customer_tier_index}</span>
                                      <span className="cust-tier-indicator">Tier {tierNum}</span>
                                    </div>
                                    <span className="cust-email-mask">
                                      {comm.customer_email ? comm.customer_email.replace(/(.{3})(.*)(@.*)/, '$1***$3') : 'Customer'}
                                    </span>
                                  </td>
                                  <td className="td-ledger-plan">
                                    <span className="plan-name-badge">
                                      {comm.plan_id ? comm.plan_id.replace(/_/g, ' ') : 'Subscription'}
                                    </span>
                                    <span className="cycle-sub-desc">
                                      {comm.billing_cycle === 'lifetime' ? 'One-time purchase' : `${comm.billing_cycle} • Cycle #${comm.payment_cycle_number}`}
                                    </span>
                                  </td>
                                  <td className="td-ledger-gross">
                                    ${Number(comm.gross_amount).toFixed(2)}
                                  </td>
                                  <td className="td-ledger-rate">
                                    <span className="rate-pill">
                                      {(Number(comm.commission_rate) * 100).toFixed(1)}%
                                    </span>
                                  </td>
                                  <td className="td-ledger-payout">
                                    <div className="payout-pills-row">
                                      <strong className="payout-amount-clean">
                                        ${Number(comm.commission_amount).toFixed(2)}
                                      </strong>
                                      <span className={`status-pill ${comm.status}`}>
                                        {comm.status === 'paid' ? '✓ Paid' : comm.status === 'pending' ? 'Pending Settlement' : comm.status}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="ledger-empty-card">
                      <Users size={24} className="text-muted" />
                      <p>No attributed transactions yet. Share your partner link or use the Test Simulator above to simulate a test order.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>

        {/* ALWAYS VISIBLE: SINGLE UNIFIED COMMISSION & TIER COMPARISON TABLE (MOVED ABOVE TEXTS) */}
        <div className="aff-unified-section">
          <div className="aff-unified-header">
            <div>
              <span className="section-eyebrow">PARTNERSHIP REVENUE MODEL</span>
              <h2 className="section-heading">Partnership Revenue Share & Tier Breakdown</h2>
              <p className="section-description">
                Earn transparent, recurring collaboration share on every client payment collected via Lemon Squeezy.
              </p>
            </div>

            {/* Filter Toggle */}
            <div className="table-filter-pill" aria-label="Filter plans">
              <button
                className={`filter-pill-btn ${tableFilter === 'all' ? 'active' : ''}`}
                onClick={() => setTableFilter('all')}
              >
                All Plans
              </button>
              <button
                className={`filter-pill-btn ${tableFilter === 'monthly' ? 'active' : ''}`}
                onClick={() => setTableFilter('monthly')}
              >
                Monthly Plans
              </button>
              <button
                className={`filter-pill-btn ${tableFilter === 'yearly' ? 'active' : ''}`}
                onClick={() => setTableFilter('yearly')}
              >
                Yearly Plans
              </button>
              <button
                className={`filter-pill-btn ${tableFilter === 'lifetime' ? 'active' : ''}`}
                onClick={() => setTableFilter('lifetime')}
              >
                Lifetime
              </button>
            </div>
          </div>

          {/* Clean Outlined Table */}
          <div className="aff-clean-table-wrapper">
            <table className="aff-master-table">
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>
                    <div className="th-header-inner">
                      <div className="th-title-row">
                        <span className="th-main-title">Plan & Retail Price</span>
                      </div>
                      <div className="th-sub-title">Catalog subscription price</div>
                    </div>
                  </th>
                  <th style={{ width: '22%' }}>
                    <div className="th-header-inner">
                      <div className="th-title-row">
                        <span className="th-main-title">Tier 1: 1–5 Customers</span>
                        <span className="th-pct-tag">35%</span>
                      </div>
                      <div className="th-sub-title">35% per customer</div>
                    </div>
                  </th>
                  <th style={{ width: '21%' }}>
                    <div className="th-header-inner">
                      <div className="th-title-row">
                        <span className="th-main-title">Tier 2: 6–15 Customers</span>
                        <span className="th-pct-tag">30%</span>
                      </div>
                      <div className="th-sub-title">30% per customer</div>
                    </div>
                  </th>
                  <th style={{ width: '21%' }}>
                    <div className="th-header-inner">
                      <div className="th-title-row">
                        <span className="th-main-title">Tier 3: 16+ Customers</span>
                        <span className="th-pct-tag">25%</span>
                      </div>
                      <div className="th-sub-title">25% per customer</div>
                    </div>
                  </th>
                  <th style={{ width: '14%' }}>
                    <div className="th-header-inner">
                      <div className="th-title-row">
                        <span className="th-main-title">Payout Term</span>
                      </div>
                      <div className="th-sub-title">Duration eligible</div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleSections.map((section) => (
                  <React.Fragment key={section.id}>
                    <tr className="category-header-row">
                      <td colSpan="5">
                        <span className="cat-label">{section.category}</span>
                      </td>
                    </tr>
                    {section.plans.map((p) => (
                      <tr key={p.name} className={p.popular ? 'row-popular' : ''}>
                        <td className="td-plan">
                          <div className="plan-cell-wrap">
                            <div className="plan-cell-title">
                              <span className="plan-mark-pill">+</span>
                              <strong>{p.name}</strong>
                              {p.popular && <span className="mini-popular-badge">Popular</span>}
                            </div>
                            <span className="plan-cell-price">{p.price}</span>
                          </div>
                        </td>

                        {/* Tier 1 */}
                        <td className="td-tier">
                          <div className="tier-cell-inner">
                            <div className="cell-main-row">
                              <span className="cell-earning-total">{p.tier1.each || p.tier1.total}</span>
                              <CalcTooltip calc={p.tier1.calc} />
                            </div>
                            <span className="cell-earning-sub">{p.tier1.totalSub || p.tier1.sub}</span>
                          </div>
                        </td>

                        {/* Tier 2 */}
                        <td className="td-tier">
                          <div className="tier-cell-inner">
                            <div className="cell-main-row">
                              <span className="cell-earning-total">{p.tier2.each || p.tier2.total}</span>
                              <CalcTooltip calc={p.tier2.calc} />
                            </div>
                            <span className="cell-earning-sub">{p.tier2.totalSub || p.tier2.sub}</span>
                          </div>
                        </td>

                        {/* Tier 3 */}
                        <td className="td-tier">
                          <div className="tier-cell-inner">
                            <div className="cell-main-row">
                              <span className="cell-earning-total">{p.tier3.each || p.tier3.total}</span>
                              <CalcTooltip calc={p.tier3.calc} />
                            </div>
                            <span className="cell-earning-sub">{p.tier3.totalSub || p.tier3.sub}</span>
                          </div>
                        </td>

                        {/* Duration */}
                        <td className="td-duration">
                          <span className="duration-tag">{p.duration}</span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* SINGLE UNIFIED CONTAINER FOR ALL PROGRAM FEATURES & CLIENT VALUE */}
        <section className="partner-comprehensive-container">
          {/* COMPONENT 1: WHITE-GLOVE SETUP FOR PARTNERS (LEADING VALUE PROPOSITION) */}
          <div className="program-block">
            <div className="onboarding-banner-grid">
              <div className="onboarding-text-side">
                <span className="section-eyebrow">WHITE-GLOVE SETUP FOR PARTNERS</span>
                <h2 className="section-heading">You Bring the Client. We Handle 100% of Training & Deployment.</h2>
                <p className="section-description">
                  As a SmushLabs Partner, you never need to learn prompt engineering, fine-tune models, or troubleshoot vector embeddings. Our internal engineering team handles the complete end-to-end rollout under 48 hours.
                </p>

                <div className="onboarding-steps-list">
                  <div className="onboarding-step-item">
                    <div className="step-number">1</div>
                    <div className="step-info">
                      <h4>Just Provide Your Client’s Website URL</h4>
                      <p>All we require is the client's public website. No proprietary credentials, API keys, or technical documents needed to get started.</p>
                    </div>
                  </div>

                  <div className="onboarding-step-item">
                    <div className="step-number">2</div>
                    <div className="step-info">
                      <h4>SmushLabs Autonomously Trains in &lt; 48 Hours</h4>
                      <p>Our autonomous crawlers extract your client's pages, services, product catalogs, and policies, training a private dedicated LLM agent with QA testing.</p>
                    </div>
                  </div>

                  <div className="onboarding-step-item">
                    <div className="step-number">3</div>
                    <div className="step-info">
                      <h4>Receive a Ready-to-Embed Code Snippet</h4>
                      <p>You receive a clean 1-line embed script tag that can be pasted directly into WordPress, Shopify, Webflow, Squarespace, or custom React/HTML code.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="onboarding-badge-side">
                <div className="delivery-card">
                  <div className="delivery-chip">
                    <Clock size={14} /> 48-HOUR GUARANTEED TURNAROUND
                  </div>
                  <h3>White-Glove Partner Deployment</h3>
                  <p>
                    Every client referred through your partner link receives priority onboarding handled directly by SmushLabs engineers.
                  </p>
                  <div className="delivery-highlights">
                    <div className="del-item"><Check size={15} /> Crawling & Vector Training</div>
                    <div className="del-item"><Check size={15} /> Custom Brand Theme Styling</div>
                    <div className="del-item"><Check size={15} /> Lead Capture Workflow Config</div>
                    <div className="del-item"><Check size={15} /> 1-Line Clean Embed Script</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* COMPONENT 2: CLIENT VALUE & CAPABILITIES */}
          <div className="program-block program-block-divider">
            <div className="section-head-center">
              <span className="section-eyebrow">CLIENT VALUE & CAPABILITIES</span>
              <h2 className="section-heading">How SmushChat Solves Your Clients' Daily Operational Bottlenecks</h2>
              <p className="section-description">
                When you introduce a client to SmushLabs, they receive a bespoke, 24/7 AI workforce that handles routine customer communication, captures high-intent leads, and drives measurable business growth.
              </p>
            </div>

            <div className="partner-pillars-grid">
              <div className="pillar-card">
                <div className="pillar-header">
                  <MessageSquare size={19} className="pillar-icon" />
                  <h3>Resolve 80%+ of Daily Inquiries</h3>
                </div>
                <p>
                  Eliminates repetitive support tickets by answering complex product specs, pricing terms, FAQs, and business policies instantly with human-level nuance—day or night.
                </p>
                <ul className="pillar-bullets">
                  <li><Check size={14} /> Zero queue wait times for visitors</li>
                  <li><Check size={14} /> Handles simultaneous chats with zero degradation</li>
                  <li><Check size={14} /> Never hallucinates or makes false claims</li>
                </ul>
              </div>

              <div className="pillar-card">
                <div className="pillar-header">
                  <Zap size={19} className="pillar-icon" />
                  <h3>Automated High-Intent Lead Capture</h3>
                </div>
                <p>
                  Turns passive website visitors into warm prospects by actively prompting for emails, phone numbers, and project specifications directly inside the conversation.
                </p>
                <ul className="pillar-bullets">
                  <li><Check size={14} /> Intelligent conversational contact forms</li>
                  <li><Check size={14} /> Real-time notification to sales teams</li>
                  <li><Check size={14} /> Direct CRM & webhook synchronization</li>
                </ul>
              </div>

              <div className="pillar-card">
                <div className="pillar-header">
                  <ShoppingBag size={19} className="pillar-icon" />
                  <h3>Live Order & Shipment Tracking</h3>
                </div>
                <p>
                  Deep e-commerce intelligence allows customers to check order statuses, tracking numbers, and delivery dates simply by providing their email or order number.
                </p>
                <ul className="pillar-bullets">
                  <li><Check size={14} /> Instant WooCommerce & Shopify lookups</li>
                  <li><Check size={14} /> Reduces "Where is my order?" tickets by 70%</li>
                  <li><Check size={14} /> Automated refund and return policy routing</li>
                </ul>
              </div>
            </div>
          </div>

          {/* COMPONENT 4: COMPLETE PLATFORM ACCESS */}
          <div className="program-block program-block-divider">
            <div className="section-head-center">
              <span className="section-eyebrow">COMPLETE PLATFORM ACCESS</span>
              <h2 className="section-heading">Enterprise Control Your Client Receives on Day One</h2>
              <p className="section-description">
                Upon enrollment, your client gets dedicated full-featured dashboard access designed for ease of use by non-technical marketing and support teams.
              </p>
            </div>

            <div className="platform-suite-grid">
              <div className="suite-card">
                <div className="suite-header">
                  <MessageSquare size={17} className="suite-icon" />
                  <h4>Monitor Live Conversations</h4>
                </div>
                <p>
                  Full transparent oversight. Inspect real-time conversations, user questions, AI responses, and visitor session journeys with instant transcripts.
                </p>
              </div>

              <div className="suite-card">
                <div className="suite-header">
                  <BarChart3 size={17} className="suite-icon" />
                  <h4>Analytics & Trending Topics</h4>
                </div>
                <p>
                  Actionable business intelligence. Uncover what customers ask most, identify missing knowledge, track resolution rates, and analyze visitor sentiment.
                </p>
              </div>

              <div className="suite-card">
                <div className="suite-header">
                  <Palette size={17} className="suite-icon" />
                  <h4>White-Label Brand Customization</h4>
                </div>
                <p>
                  Custom brand control. Adjust primary colors, font styles, bot avatar icons, chat widget position, welcome message, and custom quick-reply buttons.
                </p>
              </div>

              <div className="suite-card">
                <div className="suite-header">
                  <Database size={17} className="suite-icon" />
                  <h4>Continuous Knowledge Training</h4>
                </div>
                <p>
                  Flexible continuous learning. Upload PDFs, DOCX files, internal SOPs, or extra URLs anytime with 1-click re-indexing to instantly update bot memory.
                </p>
              </div>

              <div className="suite-card">
                <div className="suite-header">
                  <Layers size={17} className="suite-icon" />
                  <h4>WooCommerce & Multi-Channel Integrations</h4>
                </div>
                <p>
                  Seamless connectivity. Sync directly with WooCommerce, WhatsApp Business, Webhooks, and Zapier to automate workflows across existing business tools.
                </p>
              </div>

              <div className="suite-card">
                <div className="suite-header">
                  <Inbox size={17} className="suite-icon" />
                  <h4>Live Agent Inbox & Human Handoff</h4>
                </div>
                <p>
                  Seamless collaboration. When human expertise is needed, sales and support reps can jump into active AI conversations or reply directly to pending chats.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* BOTTOM SECTION: NEW AFFILIATE SIGN UP FORM */}
        <section id="affiliate-signup" className="aff-bottom-signup-section">
          <div className="bottom-signup-inner">
            <div className="bottom-signup-header">
              <span className="aff-kicker">JOIN OUR PARTNER PROGRAM</span>
              <h2 className="bottom-signup-title">Become a Partner & Get Your Unique Code</h2>
              <p className="bottom-signup-desc">
                No user account or credit card required. Fill in your details below to generate your unique partner link and earn up to 35% recurring revenue share. You can track your referrals, attributed clients, tier progress, and payouts anytime right from the tracker above using just your partner code.
              </p>
            </div>

            <form onSubmit={handleRegister} className="aff-compact-form bottom-signup-form">
              <div className="form-cols">
                <div className="field-block">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Alex Morgan"
                    value={regForm.name}
                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                  />
                </div>
                <div className="field-block">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    required
                    placeholder="alex@agency.com"
                    value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="field-block">
                <label>Custom Partner Code / Slug (Optional)</label>
                <div className="slug-box">
                  <span>smushlabs.ai/?ref=</span>
                  <input
                    type="text"
                    placeholder="e.g. agency-name"
                    value={regForm.referralCode}
                    onChange={(e) => setRegForm({ ...regForm, referralCode: e.target.value })}
                  />
                </div>
                <small>Leave blank to auto-generate a unique partner code</small>
              </div>

              <div className="form-cols">
                <div className="field-block">
                  <label>Preferred Payout Method</label>
                  <select
                    value={regForm.payoutMethod}
                    onChange={(e) => setRegForm({ ...regForm, payoutMethod: e.target.value })}
                  >
                    <option value="wise">Wise</option>
                    <option value="paypal">PayPal</option>
                    <option value="upi">UPI (India)</option>
                  </select>
                </div>
                <div className="field-block">
                  <label>
                    {regForm.payoutMethod === 'upi' ? 'UPI ID (VPA)' : regForm.payoutMethod === 'wise' ? 'Wise Email / Account' : 'PayPal Email'}
                  </label>
                  <input
                    type="text"
                    placeholder={
                      regForm.payoutMethod === 'upi'
                        ? 'e.g. yourname@okhdfcbank or phone@paytm'
                        : regForm.payoutMethod === 'wise'
                        ? 'Wise registered email or account'
                        : 'PayPal registered email'
                    }
                    value={regForm.payoutDetails}
                    onChange={(e) => setRegForm({ ...regForm, payoutDetails: e.target.value })}
                  />
                </div>
              </div>

              {regError && (
                <div className="error-badge">
                  <AlertCircle size={15} /> {regError}
                </div>
              )}

              <div className="bottom-signup-submit-wrap">
                <button type="submit" disabled={regLoading} className="btn-submit-action">
                  {regLoading ? 'Creating Partner Account...' : 'Generate My Partner Link'} <ArrowRight size={16} />
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}
